import React, { useState, useEffect } from 'react';
import { doc, updateDoc, deleteDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { getStorage, ref, deleteObject, listAll } from 'firebase/storage';
import { deleteUser, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { db } from '../firebase';
import { Icon } from './ui';
import { toast } from './feedback';
import { siteUrl } from '../native';
import { pushPermission, enablePush, openAppSettings, deleteAllDevices } from '../push';
import { useBlocked, unblockUser, deleteBlockList } from '../blocking';
import { deleteAllProgress } from '../progress';
import { deleteMeta } from '../privateMeta';

function Settings({ user, userProfile, onProfileUpdate, onLogout, showNotification }) {
  const [prefs, setPrefs] = useState(userProfile?.notificationPreferences || {emailNotifications:true,smsNotifications:false,newMatches:true,newMessages:true,studyRequests:true});
  // The toggle being saved right now (it's disabled until the write lands).
  const [savingKey, setSavingKey] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [password, setPassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const blocked = useBlocked(user?.uid);
  const unblock = async (uid, name) => {
    try { await unblockUser(user.uid, uid); toast(`${name} is unblocked.`); }
    catch (e) { toast('Could not unblock. Please try again.', { type: 'error' }); }
  };
  // iPhone app only: the system's push permission ('granted' | 'denied' | 'prompt').
  // Re-checked when the user comes back from iOS Settings.
  const [pushState, setPushState] = useState(null);
  useEffect(() => {
    const check = () => pushPermission().then(setPushState).catch(() => {});
    check();
    document.addEventListener('visibilitychange', check);
    return () => document.removeEventListener('visibilitychange', check);
  }, []);
  const turnOnPush = async () => {
    if (pushState === 'denied') { openAppSettings(); return; }
    try { await enablePush(user.uid); } catch (e) { toast('Could not turn on notifications.', { type: 'error' }); }
    setPushState(await pushPermission());
  };

  // Toggles save as soon as they change, like iOS. Only a failure says anything.
  const toggle = async k => {
    const before = prefs;
    const next = { ...prefs, [k]: !prefs[k] };
    setPrefs(next);
    setSavingKey(k);
    try {
      await updateDoc(doc(db,'users',user.uid),{notificationPreferences:next});
      onProfileUpdate({...userProfile,notificationPreferences:next});
    } catch(e) {
      setPrefs(before);
      toast('Could not save that setting. Please try again.', { type: 'error' });
    } finally { setSavingKey(null); }
  };

  const deleteAccount = async () => {
    if (confirmText !== 'DELETE') { toast('Type DELETE to confirm.', { type: 'error' }); return; }
    if (!password) { toast('Enter your password to confirm.', { type: 'error' }); return; }
    setDeleting(true);
    try {
      const cred = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, cred);
    } catch(e) {
      setDeleting(false);
      toast('Password incorrect. Your account was not deleted.', { type: 'error' });
      return;
    }
    try {
      const storage = getStorage();

      const mats = await getDocs(query(collection(db,'materials'), where('uploadedByUid','==',user.uid)));
      for (const d of mats.docs) {
        const path = d.data().storagePath;
        if (path) { try { await deleteObject(ref(storage, path)); } catch(e) { console.warn('file already gone', e); } }
        try { await deleteDoc(d.ref); } catch(e) { console.warn('doc delete failed', e); }
      }

      try {
        const folder = await listAll(ref(storage, `profilePictures/${user.uid}`));
        for (const item of folder.items) { try { await deleteObject(item); } catch(e) {} }
      } catch(e) { console.warn('no profile pictures', e); }

      try { await deleteAllDevices(user.uid); } catch(e) { console.warn('device tokens delete failed', e); }
      try { await deleteBlockList(user.uid); } catch(e) { console.warn('block list delete failed', e); }
      try { await deleteAllProgress(user.uid); } catch(e) { console.warn('progress delete failed', e); }
      try { await deleteMeta(user.uid); } catch(e) { console.warn('private meta delete failed', e); }
      try { await deleteDoc(doc(db,'users',user.uid)); } catch(e) { console.warn('profile delete failed', e); }

      await deleteUser(user);
    } catch(e) {
      console.error(e);
      setDeleting(false);
      toast('Something went wrong: ' + (e.message || 'please try again.'), { type: 'error' });
    }
  };

  const Row = ({title,desc,k}) => (
    <div className="notification-toggle">
      <div className="toggle-label"><div className="toggle-title">{title}</div><div className="toggle-description">{desc}</div></div>
      <button type="button" role="switch" aria-checked={!!prefs[k]} aria-label={title} className={`toggle-switch ${prefs[k]?'active':''}`} onClick={()=>toggle(k)} disabled={savingKey===k}><span className="toggle-slider"></span></button>
    </div>
  );

  return (
    <>
      <div className="card">
        <h2 className="card-label">Notifications</h2>
        {/* Hidden until something sends them: Email (prefs.emailNotifications is still saved as-is)
            and New Matches (no code writes a match notification yet). */}
        {pushState && (
          <div className="settings-section">
            <div className="section-label">On this iPhone</div>
            <div className="notification-toggle">
              <div className="toggle-label">
                <div className="toggle-title">Push notifications</div>
                <div className="toggle-description">{pushState === 'granted' ? 'On. The switches below choose what you get.' : pushState === 'denied' ? 'Off. Turn them on in iOS Settings → MedXMeet → Notifications.' : 'Get notified even when MedXMeet is closed.'}</div>
              </div>
              {pushState === 'granted'
                ? <span className="small">On</span>
                : <button type="button" className="btn-small secondary" onClick={turnOnPush}>{pushState === 'denied' ? 'Open Settings' : 'Turn on'}</button>}
            </div>
          </div>
        )}
        <div className="settings-section">
          <Row title="New Messages" desc="When someone messages you or shares a document" k="newMessages" />
          <Row title="Study Requests" desc="When someone requests, accepts or declines a session" k="studyRequests" />
        </div>
      </div>

      <div className="card">
        <h2 className="card-label">Blocked users</h2>
        {Object.keys(blocked).length === 0
          ? <p className="muted small">You haven't blocked anyone. Block someone from their chat or match card; they won't be able to contact you.</p>
          : Object.entries(blocked).sort((a, b) => a[1].localeCompare(b[1])).map(([uid, name]) => (
            <div key={uid} className="kv-row blocked-row">
              <div>{name}</div>
              <button type="button" className="btn-small secondary" onClick={() => unblock(uid, name)}>Unblock</button>
            </div>
          ))}
      </div>

      <div className="card">
        <h2 className="card-label">Account</h2>
        {[['Email',user?.email],['Member since',user?.metadata?.creationTime?new Date(user.metadata.creationTime).toLocaleDateString('en-US',{month:'long',year:'numeric'}):'Unknown']].map(([l,v])=>(
          <div key={l} className="kv-row">
            <div className="small">{l}</div>
            <div>{v}</div>
          </div>
        ))}
      </div>

      <button className="card row-link" onClick={onLogout}>
        <Icon name="logout" />
        <span className="row-link-text">Log out</span>
      </button>

      <div className="card">
        <h2 className="card-label danger-title">Delete Account</h2>
        {!showDelete ? (
          <>
            <p className="muted" style={{marginBottom:'16px'}}>
              Permanently deletes your profile, photo, and documents you've shared. Messages and sessions you've exchanged stay in your partners' history. This cannot be undone.
            </p>
            <button className="btn-small btn-decline" onClick={()=>setShowDelete(true)}>Delete my account</button>
          </>
        ) : (
          <>
            <div className="danger-box">
              <strong>This is permanent.</strong>
              <ul>
                <li>Your profile and photo will be deleted</li>
                <li>Documents you shared will be deleted for everyone</li>
                <li>You will be signed out and cannot log back in</li>
              </ul>
            </div>
            <div className="form-group">
              <label>Type DELETE to confirm</label>
              <input type="text" value={confirmText} onChange={e=>setConfirmText(e.target.value)} placeholder="DELETE" autoComplete="off" />
            </div>
            <div className="form-group">
              <label>Your password</label>
              <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" />
            </div>
            <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
              <button className="btn-small btn-decline" onClick={deleteAccount} disabled={deleting || confirmText !== 'DELETE' || !password}>
                {deleting ? 'Deleting…' : 'Permanently delete my account'}
              </button>
              <button className="btn-small secondary" onClick={()=>{setShowDelete(false);setConfirmText('');setPassword('');}} disabled={deleting}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
      <div className="card muted small" style={{textAlign:'center'}}>
        Questions? Email us at <a href="mailto:support@medxmeet.com">support@medxmeet.com</a>
        <div style={{marginTop:'8px'}}>
          <a href={siteUrl('/support.html')} target="_blank" rel="noopener noreferrer">Support</a> · <a href={siteUrl('/privacy.html')} target="_blank" rel="noopener noreferrer">Privacy Policy</a> · <a href={siteUrl('/terms.html')} target="_blank" rel="noopener noreferrer">Terms of Use</a>
        </div>
      </div>
    </>
  );
}
export default Settings;